import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ChevronDown } from 'lucide-react';
import type { SecurityExploitability } from '@data/utils/types';
import { Input } from '@ui/components/ui/input';
import { SEVERITY_COLORS, SecurityIssueRow } from './sources/types';

const DAY_MS = 86_400_000;

type Severity = SecurityIssueRow['severity'];
type Filter = 'all' | Severity;

const SEVERITIES: Severity[] = ['critical', 'high', 'medium', 'low'];

const SEVERITY_LABEL: Record<Severity, string> = {
  critical: 'productDashboard.severityCritical',
  high: 'productDashboard.severityHigh',
  medium: 'productDashboard.severityMedium',
  low: 'productDashboard.severityLow',
};

const SEVERITY_TEXT: Record<Severity, string> = {
  critical: 'text-red-700 dark:text-red-400',
  high: 'text-red-600 dark:text-red-300',
  medium: 'text-amber-700 dark:text-amber-300',
  low: 'text-yellow-700 dark:text-yellow-300',
};

// Fixed tracks, not `auto`: header and rows are separate grids, so `auto` would size each to its own text.
const ROW_GRID =
  'grid grid-cols-[minmax(0,1fr)_5.5rem_1rem] sm:grid-cols-[minmax(0,1fr)_9rem_5rem_5.5rem_1rem] items-center gap-x-3';

const daysOpen = (iso?: string | null): number | null =>
  iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / DAY_MS)) : null;

/** Aikido estimates in minutes; a day here is a working day of eight hours. */
export const resolveTime = (minutes: number, t: TFunction): string => {
  if (minutes < 60) return t('productDashboard.securityMinutes', { count: minutes });
  if (minutes < 8 * 60) return t('productDashboard.securityHours', { count: Math.round((minutes / 60) * 2) / 2 });
  return t('productDashboard.securityDays', { count: Math.round((minutes / 480) * 2) / 2 });
};

/** Every dependency issue says where attackers stand; any other only when there is exploit code. */
const shownExploit = (issue: SecurityIssueRow): SecurityExploitability | null => {
  if (!issue.exploitability) return null;
  if (issue.exploitability === 'no_known_exploit' && !issue.package) return null;
  return issue.exploitability;
};

const Fact = ({ label, children }: { label: string; children: React.ReactNode }): JSX.Element => (
  <div className="min-w-0">
    <p className="mb-1 text-[0.72rem] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
    <div className="text-sm text-foreground">{children}</div>
  </div>
);

const Box = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <span className="mb-1 mr-1 inline-block break-all rounded-md border border-border bg-muted px-1.5 font-mono text-xs">
    {children}
  </span>
);

const Where = ({ issue }: { issue: SecurityIssueRow }): JSX.Element => {
  const { t } = useTranslation();

  // A client gets no locations, so the count stands in for them.
  if (!issue.locations.length) {
    return (
      <>
        {issue.package
          ? t('productDashboard.securityInDependencies')
          : t('productDashboard.securityInPlaces', { count: issue.places })}
      </>
    );
  }

  const hidden = issue.places - issue.locations.length;
  return (
    <ul className="space-y-0.5">
      {issue.locations.map(location => (
        <li key={`${location.file}:${location.line ?? ''}`} className="break-all font-mono text-xs">
          {location.file}
          {location.line ? `:${location.line}` : ''}
        </li>
      ))}
      {hidden > 0 && (
        <li className="text-xs text-muted-foreground">{t('productDashboard.securityMorePlaces', { count: hidden })}</li>
      )}
    </ul>
  );
};

const Detail = ({ issue }: { issue: SecurityIssueRow }): JSX.Element => {
  const { t } = useTranslation();
  const days = daysOpen(issue.firstDetectedAt);
  const exploit = shownExploit(issue);

  return (
    <div className="grid gap-x-6 gap-y-3.5 pb-4 pr-1 pt-0.5 sm:grid-cols-[1.4fr_1fr]">
      {issue.howToFix && <Fact label={t('productDashboard.securityHowToFix')}>{issue.howToFix}</Fact>}
      {issue.minutesToFix != null && (
        <Fact label={t('productDashboard.securityTimeToResolve')}>{resolveTime(issue.minutesToFix, t)}</Fact>
      )}
      {(issue.places > 0 || issue.package) && (
        <Fact label={t('productDashboard.securityWhere')}>
          <Where issue={issue} />
        </Fact>
      )}
      {exploit && (
        <Fact label={t('productDashboard.securityExploits')}>{t(`productDashboard.securityExploit_${exploit}`)}</Fact>
      )}
      {days !== null && (
        <Fact label={t('productDashboard.securityFirstFound')}>
          {t('productDashboard.securityDaysAgo', { count: days })}
        </Fact>
      )}
      {issue.cveIds.length > 0 && (
        <Fact label={t('productDashboard.securityCveIds')}>
          {issue.cveIds.map(id => (
            <Box key={id}>{id}</Box>
          ))}
        </Fact>
      )}
    </div>
  );
};

// Animates the detail's height open and shut; it mounts on the first open and stays for the close.
const Collapse = ({ isOpen, children }: { isOpen: boolean; children: React.ReactNode }): JSX.Element => {
  const [hasOpened, setHasOpened] = useState(isOpen);
  if (isOpen && !hasOpened) setHasOpened(true);

  return (
    <div
      aria-hidden={!isOpen}
      className={cx(
        'grid transition-[grid-template-rows,visibility] duration-300 ease-out motion-reduce:transition-none',
        isOpen ? 'visible grid-rows-[1fr]' : 'invisible grid-rows-[0fr]',
      )}
    >
      <div className="min-h-0 overflow-hidden">{hasOpened && children}</div>
    </div>
  );
};

const IssueRow = ({
  issue,
  isOpen,
  onToggle,
}: {
  issue: SecurityIssueRow;
  isOpen: boolean;
  onToggle: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const days = daysOpen(issue.firstDetectedAt);
  const installed = issue.package?.installedVersion;
  const fixedIn = issue.package?.fixedIn;
  const version = installed ? (
    <>
      {installed}
      {fixedIn && (
        <>
          {' '}
          → <span className="text-foreground">{fixedIn}</span>
        </>
      )}
    </>
  ) : (
    issue.places > 0 && t('productDashboard.securityPlaces', { count: issue.places })
  );

  return (
    <div className="border-b border-border/60 last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className={cx(ROW_GRID, 'w-full py-1.5 text-left text-sm')}
      >
        <span className="min-w-0">
          <span className="block truncate text-foreground">{issue.title}</span>
          <span className="block truncate text-xs text-muted-foreground sm:hidden">
            {issue.kind}
            {version && <> · {version}</>}
          </span>
        </span>
        <span className="hidden truncate text-right font-mono text-xs text-muted-foreground sm:block">{version}</span>
        <span className="hidden whitespace-nowrap text-right tabular-nums text-foreground sm:block">
          {days !== null && t('productDashboard.securityOpenFor', { count: days })}
        </span>
        <span className={cx('whitespace-nowrap text-right font-medium', SEVERITY_TEXT[issue.severity])}>
          {issue.severityLabel}
        </span>
        <ChevronDown
          className={cx(
            'size-4 shrink-0 text-muted-foreground transition-transform duration-300 ease-out',
            isOpen && 'rotate-180',
          )}
          aria-hidden
        />
      </button>
      <Collapse isOpen={isOpen}>
        <Detail issue={issue} />
      </Collapse>
    </div>
  );
};

/** Every open Aikido issue, worst first, filterable by severity and searchable, each opening onto how to fix it. */
const SecurityIssues = ({ issues }: { issues: SecurityIssueRow[] }): JSX.Element => {
  const { t } = useTranslation();
  const [openIds, setOpenIds] = useState<Set<number>>(() => new Set());
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [atBottom, setAtBottom] = useState(false);

  const needle = query.trim().toLowerCase();
  const shown = issues.filter(
    issue =>
      (filter === 'all' || issue.severity === filter) &&
      [issue.title, issue.kind, issue.package?.name ?? ''].some(text => text.toLowerCase().includes(needle)),
  );
  const countOf = (key: Filter): number =>
    key === 'all' ? issues.length : issues.filter(issue => issue.severity === key).length;

  const updateFade = (el: HTMLDivElement | null): void => {
    if (el) setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 8);
  };
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [filter, needle]);
  useEffect(() => {
    updateFade(scrollRef.current);
  }, [issues, filter, needle, openIds]);

  const toggle = (id: number): void =>
    setOpenIds(current => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="search"
          value={query}
          onChange={event => setQuery(event.target.value)}
          placeholder={t('productDashboard.securitySearch')}
          aria-label={t('productDashboard.securitySearch')}
          className="min-w-40 flex-1 print:hidden"
        />
        <div
          className="flex flex-wrap gap-1.5 print:hidden"
          role="group"
          aria-label={t('productDashboard.securityFilter')}
        >
          {(['all', ...SEVERITIES] as Filter[]).map(key => (
            <button
              key={key}
              type="button"
              aria-pressed={filter === key}
              onClick={() => setFilter(key)}
              className={cx(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors duration-200',
                filter === key
                  ? 'border-foreground/25 bg-muted text-foreground'
                  : 'border-border text-muted-foreground hover:bg-muted/60',
              )}
            >
              {key !== 'all' && (
                <span
                  aria-hidden
                  className="size-2 shrink-0 rounded-full"
                  style={{ background: SEVERITY_COLORS[key] }}
                />
              )}
              {key === 'all' ? t('productDashboard.securityAll') : t(SEVERITY_LABEL[key])}
              <span className="tabular-nums">{countOf(key)}</span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className={cx(ROW_GRID, 'mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground')}>
          <p>{t('productDashboard.securityIssue')}</p>
          <p className="hidden text-right sm:block">{t('productDashboard.securityVersion')}</p>
          <p className="hidden text-right sm:block">{t('productDashboard.securityOpenForHeader')}</p>
          <p className="text-right">{t('productDashboard.securitySeverity')}</p>
        </div>
        <div className="relative">
          <div
            ref={scrollRef}
            onScroll={event => updateFade(event.currentTarget)}
            // One height for every product and filter, so the card never changes size.
            className="h-60 overflow-y-auto pr-1 print:h-auto print:overflow-visible"
          >
            {shown.map(issue => (
              <IssueRow key={issue.id} issue={issue} isOpen={openIds.has(issue.id)} onToggle={() => toggle(issue.id)} />
            ))}
            {!shown.length && (
              <p className="py-3 text-sm text-muted-foreground">
                {needle
                  ? t('productDashboard.securityNoMatch', { query: query.trim() })
                  : t('productDashboard.securityGroupEmpty')}
              </p>
            )}
          </div>
          {/* Bottom fade: there is more to scroll; gone once at the bottom. */}
          <div
            className={cx(
              'pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-card to-transparent transition-opacity duration-200 print:hidden',
              atBottom && 'opacity-0',
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default SecurityIssues;
